import { FRAME_ASPECT, WORLD_H } from '../engine/constants';
import { toNorm, toWorld, validateLevel, type Level, type Pt } from '../engine/level';
import { GameWorld, type Snapshot } from '../engine/world';
import { Renderer } from '../game/renderer';
import { fitRotation, watchViewport } from '../game/orient';
import { clientToLocal } from '../game/input';

type Sel = { kind: 'vertex'; poly: number; i: number } | { kind: 'ball'; which: 'blue' | 'pink' } | null;

/** `#edit/<n>`. 원본 프레임(/frames/<n>.png, dev 전용)을 배경으로 꼭짓점·공을 드래그. */
export function openEditor(host: HTMLElement, initial: Level): { destroy(): void } {
  let level: Level = JSON.parse(JSON.stringify(initial));
  const history: string[] = [];
  let sel: Sel = null;
  let activePoly = -1;
  let bgAlpha = 0.4;
  const root = document.createElement('div');
  root.className = 'editor';
  root.innerHTML = `
    <canvas></canvas>
    <div class="bar">
      <button data-a="back">‹ 목록</button>
      <span>#${level.id}</span>
      <button data-a="rect">+사각형</button>
      <button data-a="delpoly">다각형 삭제</button>
      <button data-a="rminus">r−</button><button data-a="rplus">r+</button>
      <button data-a="bg">배경 ${Math.round(bgAlpha * 100)}%</button>
      <button data-a="undo">undo</button>
      <button data-a="save">저장(복사+다운로드)</button>
      <button data-a="test">▶ 테스트</button>
      <span class="msg"></span>
    </div>`;
  host.replaceChildren(root);
  const canvas = root.querySelector('canvas')!;
  const msg = root.querySelector<HTMLElement>('.msg')!;
  const r = new Renderer(canvas);
  const img = new Image();
  let imgOk = false;
  img.onload = () => { imgOk = true; draw(); };
  img.src = `/frames/${level.id}.png`;

  const fit = () => { const [w, h] = fitRotation(root); r.resize(w, h, window.devicePixelRatio || 1); draw(); };
  const unwatch = watchViewport(fit);

  function snapshot(): Snapshot {
    // 물리 세계를 만들어 첫 스냅샷만 쓴다(step 안 함) — 기믹 렌더링을 게임과 똑같이
    try { return new GameWorld(level).snapshot(); } catch { return new GameWorld({ ...level, obstacles: [] }).snapshot(); }
  }

  function draw() {
    r.draw(snapshot(), null);
    const { ctx, vp } = r;
    ctx.setTransform(r.dpr, 0, 0, r.dpr, 0, 0);
    if (imgOk) {
      // 프레임은 854×416 = 정규화 (FRAME_ASPECT × 1). 월드 사각형에 맞춰 깐다(스플래시 프레임 = 왼쪽 절반).
      const tl = vp.toScreen([0, WORLD_H]), br = vp.toScreen([FRAME_ASPECT * WORLD_H, 0]);
      ctx.globalAlpha = bgAlpha;
      const half = img.width > img.height * FRAME_ASPECT * 1.5 ? img.width / 2 : img.width; // 두 장 hstack 이면 왼쪽만
      ctx.drawImage(img, 0, 0, half, img.height, tl[0], tl[1], br[0] - tl[0], br[1] - tl[1]);
      ctx.globalAlpha = 1;
    }
    // 핸들
    level.obstacles.forEach((o, pi) => {
      ctx.strokeStyle = pi === activePoly ? '#e91e63' : '#2a9d8f';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      o.pts.forEach((p, i) => { const s = vp.toScreen(toWorld(p)); i ? ctx.lineTo(s[0], s[1]) : ctx.moveTo(s[0], s[1]); });
      ctx.closePath(); ctx.stroke();
      o.pts.forEach((p, i) => {
        const s = vp.toScreen(toWorld(p));
        const on = sel?.kind === 'vertex' && sel.poly === pi && sel.i === i;
        ctx.fillStyle = on ? '#e91e63' : '#fff'; ctx.strokeStyle = '#2a9d8f';
        ctx.fillRect(s[0] - 5, s[1] - 5, 10, 10); ctx.strokeRect(s[0] - 5, s[1] - 5, 10, 10);
      });
    });
    const errs = validateLevel(level);
    msg.textContent = errs.length ? `⚠ ${errs.join(', ')}` : `${level.obstacles.length} 다각형 · r ${level.balls.r.toFixed(3)}`;
  }

  function push() { history.push(JSON.stringify(level)); if (history.length > 50) history.shift(); }

  function hit(sx: number, sy: number): Sel {
    const w = r.vp.toWorld(sx, sy);
    const tol = 12 / r.vp.scale;
    for (const which of ['blue', 'pink'] as const) {
      const b = toWorld(level.balls[which]);
      if (Math.hypot(b[0] - w[0], b[1] - w[1]) < Math.max(tol, level.balls.r * WORLD_H)) return { kind: 'ball', which };
    }
    for (let pi = 0; pi < level.obstacles.length; pi++) {
      const pts = level.obstacles[pi]!.pts;
      for (let i = 0; i < pts.length; i++) {
        const p = toWorld(pts[i]!);
        if (Math.hypot(p[0] - w[0], p[1] - w[1]) < tol) return { kind: 'vertex', poly: pi, i };
      }
    }
    return null;
  }

  function polyAt(sx: number, sy: number): number {
    const n = toNorm(r.vp.toWorld(sx, sy));
    for (let pi = level.obstacles.length - 1; pi >= 0; pi--) {
      const poly = level.obstacles[pi]!.pts;
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[i]!, b = poly[j]!;
        if ((a[1] > n[1]) !== (b[1] > n[1]) && n[0] < ((b[0] - a[0]) * (n[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
      }
      if (inside) return pi;
    }
    return -1;
  }

  function edgeAt(sx: number, sy: number): { poly: number; i: number } | null {
    const w = r.vp.toWorld(sx, sy);
    const tol = 10 / r.vp.scale;
    for (let pi = 0; pi < level.obstacles.length; pi++) {
      const pts = level.obstacles[pi]!.pts.map(toWorld);
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i]!, b = pts[(i + 1) % pts.length]!;
        const abx = b[0] - a[0], aby = b[1] - a[1], l2 = abx * abx + aby * aby;
        let t = l2 ? ((w[0] - a[0]) * abx + (w[1] - a[1]) * aby) / l2 : 0; t = Math.max(0, Math.min(1, t));
        if (Math.hypot(w[0] - (a[0] + t * abx), w[1] - (a[1] + t * aby)) < tol) return { poly: pi, i };
      }
    }
    return null;
  }

  let dragging = false;
  let lastTap = 0;
  const pos = (e: PointerEvent): [number, number] => clientToLocal(root, e.clientX, e.clientY);
  canvas.addEventListener('pointerdown', (e) => {
    const [sx, sy] = pos(e);
    const now = performance.now();
    const dbl = now - lastTap < 300; lastTap = now;
    sel = hit(sx, sy);
    if (dbl) {
      if (sel?.kind === 'vertex') {
        const o = level.obstacles[sel.poly]!;
        if (o.pts.length > 3) { push(); o.pts.splice(sel.i, 1); }
        sel = null; draw(); return;
      }
      const ed = edgeAt(sx, sy);
      if (ed) {
        push();
        const n = toNorm(r.vp.toWorld(sx, sy));
        level.obstacles[ed.poly]!.pts.splice(ed.i + 1, 0, [n[0], n[1]]);
        sel = { kind: 'vertex', poly: ed.poly, i: ed.i + 1 }; activePoly = ed.poly; draw(); return;
      }
    }
    if (sel) { push(); dragging = true; canvas.setPointerCapture(e.pointerId); if (sel.kind === 'vertex') activePoly = sel.poly; }
    else activePoly = polyAt(sx, sy);
    draw();
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!dragging || !sel) return;
    const [sx, sy] = pos(e);
    const n = toNorm(r.vp.toWorld(sx, sy));
    const p: Pt = [Math.round(n[0] * 1000) / 1000, Math.round(n[1] * 1000) / 1000];
    if (sel.kind === 'ball') level.balls[sel.which] = p;
    else level.obstacles[sel.poly]!.pts[sel.i] = p;
    draw();
  });
  const up = () => { dragging = false; };
  canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);

  root.querySelector('.bar')!.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).dataset.a;
    if (!a) return;
    if (a === 'back') { location.hash = ''; return; }
    if (a === 'rect') { push(); level.obstacles.push({ pts: [[0.9, 0.4], [1.15, 0.4], [1.15, 0.5], [0.9, 0.5]] }); activePoly = level.obstacles.length - 1; }
    if (a === 'delpoly' && activePoly >= 0) { push(); level.obstacles.splice(activePoly, 1); activePoly = -1; sel = null; }
    if (a === 'rminus') { push(); level.balls.r = Math.round((level.balls.r - 0.001) * 1000) / 1000; }
    if (a === 'rplus') { push(); level.balls.r = Math.round((level.balls.r + 0.001) * 1000) / 1000; }
    if (a === 'bg') { bgAlpha = bgAlpha >= 0.8 ? 0 : bgAlpha + 0.2; (e.target as HTMLElement).textContent = `배경 ${Math.round(bgAlpha * 100)}%`; }
    if (a === 'undo') { const h = history.pop(); if (h) level = JSON.parse(h); }
    if (a === 'save') {
      const out = { ...level, src: { ...(level.src ?? { t: 0, flags: [] }), flags: [] } };
      const json = JSON.stringify(out);
      navigator.clipboard?.writeText(json).catch(() => {});
      const blob = new Blob([json], { type: 'application/json' });
      const aEl = document.createElement('a'); aEl.href = URL.createObjectURL(blob); aEl.download = `${level.id}.json`; aEl.click();
      msg.textContent = `복사·다운로드: tools/trace/overrides/${level.id}.json`;
      return;
    }
    if (a === 'test') { sessionStorage.setItem('dots-draw.draft', JSON.stringify(level)); location.hash = `play/${level.id}?draft=1`; return; }
    draw();
  });

  return { destroy() { unwatch(); root.remove(); } };
}
